import { darken, lighten, setSaturation, transparentize } from "polished";
import { createTheme, createStyles as muiCreateStyles, Theme } from "@material-ui/core";
import { StyleRules } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";

export { withStyles } from "@material-ui/core";
import { getCurrentTenant } from "../tenant/tenant";

import logoSmallSrc from "./bonliva-logo.svg";
import logoLargeSrc from "./bonliva-logo-payoff.svg";

export const bonliva0 = "#ff857a";
const bonliva1 = "#26A69A";

export const wcc0 = "#058ec1";
const wcc1 = "#67cc3d";

const appBarColor = "#fff";
const defaultTextColor = "#404040";
const backgroundColor = "#fafafa";
const darkBackgroundColor = "#5b5b5b";
const primaryColor = getCurrentTenant() === "bonliva-ab" ? bonliva0 : wcc0;
const secondaryColor = getCurrentTenant() === "bonliva-ab" ? bonliva1 : wcc1;
const alterColor = "#ff9800";
const dangerColor = "#F65629";
const descriptionColor = "#808080";
const successColor = "#02BB9C";
const neutralColor = "#7e67b4";
const separatorColor = "#e0e0e0";
const disabledColor = "#e8e8e826";

const primaryBackgroundColor = transparentize(0.8, primaryColor);
const secondaryBackgroundColor = transparentize(0.8, secondaryColor);

const mainMenuTextColor = "#6b6b6b";
const mainMenuActiveColor = primaryColor;

const paletteType = "light" as string;

const todoOwnColor = lighten(0.1, "#F7F3E3");
const todoAssignedToMeColor = lighten(0.2, "#FABF12");
const todoAssignedByMeColor = lighten(0.2, "#14555A");
const outlookEventColor = lighten(0.2, "#e6884f");

export const themeObject = {
  lighten: (color) =>
    paletteType === "dark"
      ? setSaturation(0.5, darken(0.1, color))
      : setSaturation(0.5, lighten(0.3, color)),

  appBar: {
    background: appBarColor,
    color: defaultTextColor
  },

  mainMenu: {
    textColor: {
      default: mainMenuTextColor,
      active: mainMenuActiveColor
    },
    iconsColor: {
      default: mainMenuTextColor,
      active: mainMenuActiveColor
    }
  },

  // TODO: let's move these to .palette?
  bodyMainColor: defaultTextColor,
  descriptionColor,
  separatorColor,

  avatar: {
    userSelect: "none",
    cursor: "pointer"
  },
  comments: {
    commentbox: {
      background: backgroundColor
    },
    sticky: {
      background: "#fff5e4"
    }
  },
  weeks: {
    spacing: 34,
    radius: 28,
    borderRadius: "50%",
    busy: {
      background: secondaryColor,
      text: "#fff"
    }
  },

  avatars: {
    micro: { height: 16, width: 16 },
    mini: { height: 24, width: 24 },
    small: { height: 32, width: 32 },
    medium: { height: 48, width: 48 },
    big: { height: 96, width: 96 },
    large: { height: 128, width: 128 }
  },

  radius: 2,

  dateFormat: "YYYY-MM-DD",
  dateFormatUnicode: "yyyy-MM-dd",

  dateTimeFormat: "YYYY-MM-DD HH:mm",
  dateTimeFormatUnicode: "yyyy-MM-dd HH:mm",

  fixedGrid: {
    maxWidth: 1200,
    marginRight: "auto",
    marginLeft: "auto"
  },

  fontWeight: {
    light: 300,
    medium: 400,
    bold: 500,
    xBold: 600,
    xxBold: 800
  },

  logo: {
    base: {
      backgroundPosition: "left center",
      backgroundRepeat: "no-repeat"
    },

    small: {
      backgroundImage: `url(${logoSmallSrc})`,
      width: 74,
      height: 19
    },

    medium: {
      backgroundImage: `url(${logoSmallSrc})`,
      width: 148,
      height: 38
    },

    large: {
      backgroundImage: `url(${logoLargeSrc})`,
      width: 222,
      height: 57
    }
  },

  sidebar: {
    defaultWidth: 250,
    narrowWidth: 48,
    backgroundColor: "",
    color: ""
  },

  mixins: {
    childrenMarginRight: (v) => ({
      "& > *:not(:last-child)": {
        marginRight: v
      }
    }),

    childrenMarginBottom: (v) => ({
      "& > *:not(:last-child)": {
        marginBottom: v
      }
    }),

    childrenMarginAll: (v) => ({
      "& > *": { margin: v }
    }),

    noCascadeCursor: () => ({
      cursor: "pointer",

      "& a, & span, & p, & h1, & h2, & h3, & h4, & h5, & h6": {
        cursor: "auto"
      }
    }),

    fullAbsolute: {
      position: "absolute" as any,
      right: 0,
      top: 0,
      bottom: 0,
      left: 0
    },

    fullFixed: {
      position: "fixed",
      right: 0,
      top: 0,
      bottom: 0,
      left: 0
    },

    modal: {
      alignItems: "center",
      justifyContent: "center"
    },

    withBorderBottom: (color: string) => ({
      "&:after": {
        backgroundImage: `linear-gradient(90deg, ${color}, ${color} 75%, transparent 75%, transparent 100%)`,
        backgroundSize: `5px 1px`,
        content: "' '",
        display: "block",
        height: 1,
        maxHeight: 1,
        position: "relative" as any,
        top: -2
      }
    }),

    withOverlay: (color = transparentize(0.8, "gray")) => ({
      position: "relative",

      "&:before": {
        backgroundColor: color,
        bottom: 0,
        content: "' '", // this is an essential part here
        display: "block",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 1
      }
    }),

    buttonGroup: {
      display: "flex",

      "& > button:first-child": {
        borderRadius: 0,
        borderBottomLeftRadius: 4
      },

      "& > button": {
        flex: 1,
        borderRadius: 0
      },

      "& > button:last-child": {
        borderRadius: 0,
        borderBottomRightRadius: 4
      }
    }
  },

  palette: {
    type: paletteType,

    background: {
      default: backgroundColor,
      active: transparentize(0.9, secondaryColor)
    },

    primary: {
      background: primaryBackgroundColor,
      main: primaryColor,
      contrast: "white",
      dark: darkBackgroundColor
    },

    secondary: {
      background: secondaryBackgroundColor,
      main: secondaryColor,
      contrast: "white"
    },

    alter: {
      main: alterColor,
      contrast: "black"
    },

    error: {
      main: dangerColor,
      background: transparentize(0.9, dangerColor)
    },

    info: {
      background: "#b0b0b0",
      text: "#202020",
      main: "#202020"
    },

    /**
     * palette.error ?
     */
    danger: {
      main: dangerColor
    },

    neutral: {
      main: neutralColor
    },

    success: {
      main: successColor
    },

    disabled: {
      main: disabledColor
    },

    text: {
      main: defaultTextColor,
      secondary: "rgba(0, 0, 0, 0.54)"
    },
    todos: {
      ownItem: todoOwnColor,
      assignedToMe: todoAssignedToMeColor,
      assignedByMe: todoAssignedByMeColor,
      outlookEvent: outlookEventColor,
      other: ""
    },
    secondaryLight: "#26a69a",
    secondaryDark: "#00766c"
  },

  overrides: {
    MuiInput: {},
    MuiButton: {
      containedPrimary: {
        color: "#fff"
      }
    },

    MuiTypography: {
      body1: {
        color: defaultTextColor
      },

      body2: {
        color: defaultTextColor
      },

      subheading: {
        color: defaultTextColor
      },

      title: {
        color: defaultTextColor
      },

      headline: {
        color: defaultTextColor
      }
    },

    MuiTooltip: {
      tooltip: {
        fontSize: "1,2em"
      }
    },

    MuiTableCell: {
      head: {
        fontSize: 14,
        color: descriptionColor
      }
    }
  }
};

export const themeGlobals = {
  "@global": {
    "*::-webkit-scrollbar": {
      width: 8,
      height: 8,
      backgroundColor: "transparent"
    },

    "*::-webkit-scrollbar-track": {
      backgroundColor: "transparent"
    },

    "*::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: "4px"
    }
  }
};

export type ThemeObjectType = typeof themeObject;
export type ThemeType = ThemeObjectType & Theme;
export type ThemeFunc = (theme: ThemeType) => {};

type Unpacked<T> = T extends Array<infer U>
  ? U
  : T extends (...args: any[]) => infer U
  ? U
  : T extends Promise<infer V>
  ? V
  : T;
type Partial<T> = { [P in keyof T]: string };
export type Classes<T> = Partial<Unpacked<T>>;
export type WithStyles<T> = { classes: Classes<T> };
export const bonlivaTheme = createTheme({
  typography: { useNextVariants: true },
  ...themeObject
} as any);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createStyles = <C extends string, T>(
  cb: (theme: ThemeType) => { [index: string]: CSSProperties }
) => muiCreateStyles(cb) as StyleRules<C>;
